import React, { useState } from 'react';
import { IconAvatar } from '@components/icons';
import styles from './Avatar.module.scss';

export type AvatarVariant = 'image' | 'icon' | 'initials';

export interface AvatarProps {
  /** Avatar variant */
  variant?: AvatarVariant;
  /** Image source URL (required when variant is 'image') */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Initials to display (1-2 characters, used when variant is 'initials') */
  initials?: string;
  /** Initials to show if the image fails to load (e.g. Gravatar 404) */
  fallbackInitials?: string;
  /** Additional CSS classes */
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  variant = 'icon',
  src,
  alt = 'Avatar',
  initials,
  fallbackInitials,
  className = '',
}) => {
  const [imgFailed, setImgFailed] = useState(false);

  // Determine the effective variant
  const hasSrc = src && !imgFailed;
  const effectiveInitials = initials ?? (imgFailed ? fallbackInitials : undefined);
  const effectiveVariant = hasSrc ? 'image' : effectiveInitials ? 'initials' : variant;

  const isImageVariant = effectiveVariant === 'image' && hasSrc;
  const isInitialsVariant = effectiveVariant === 'initials' && effectiveInitials;

  return (
    <div
      className={`${styles.container} ${styles[effectiveVariant]} ${className}`}
      {...(!isImageVariant && { role: 'img', 'aria-label': alt })}
    >
      {isImageVariant ? (
        <img
          className={styles.img}
          src={src}
          alt={alt}
          onError={() => setImgFailed(true)}
        />
      ) : isInitialsVariant ? (
        <span className={styles.initialsText}>
          {effectiveInitials.slice(0, 2).toUpperCase()}
        </span>
      ) : (
        <IconAvatar size={32} color="var(--color-grey-neutral)" />
      )}
    </div>
  );
};
